/**
 * Shared predicate every Google Maps script/Loader construction site (and the Air Quality request
 * builder) must consult before doing any work with the runtime-delivered browser API key. An empty,
 * absent, or whitespace-only key means the key was never configured (or the runtime-config fetch
 * failed/timed out) -- callers must skip script injection / Loader construction / the API request
 * entirely rather than send a request with a malformed `key=` value.
 */
export function shouldLoadGoogleMaps(apiKey: string | undefined | null): boolean {
  return typeof apiKey === 'string' && apiKey.trim().length > 0;
}

// Module-level once-latch: the arch-review pins "exactly ONE console.warn" for the missing-key
// posture -- a single signal per app lifetime, not one per call site. Without this, every one of
// the 6 real consumer sites that independently hits an empty key would each log its own warning
// (up to 6 near-duplicate lines for one underlying misconfiguration).
let hasWarnedMissingKey = false;

/**
 * One consistent, explicit console warning for the FIRST site (across the whole app lifetime) that
 * skips on a missing key -- so the app boots silently in the sense of "no crash, no blocked UI" but
 * never silently in the sense of "no signal that maps are unavailable" (the eStream maps outage
 * lesson: a silent guard hid a real misconfiguration). Every call after the first is a deliberate
 * no-op: the key is still missing, but repeating the warning adds noise, not signal. `context` names
 * the feature that was skipped, e.g. "Google Maps script injection".
 */
export function warnGoogleMapsKeyMissing(context: string): void {
  if (hasWarnedMissingKey) {
    return;
  }
  hasWarnedMissingKey = true;
  console.warn(`Google Maps API key is not configured -- skipping ${context}.`);
}

/** Test-only: clears the once-latch so each spec can assert warn behavior from a clean slate. */
export function resetGoogleMapsKeyMissingWarning(): void {
  hasWarnedMissingKey = false;
}
